<!-- src/components/ActivitiesHeatmap.vue -->
<template>
  <div>
    <!-- Toolbar -->
    <div class="space-y-3">
      <div class="flex justify-center">
        <div class="flex items-center gap-2 text-xs text-gray-600">
          <label class="font-semibold">Sort by</label>
          <select
            v-model="barSortKey"
            class="rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"
          >
            <option value="name">
              Name
            </option>
            <option value="billable">
              Billable %
            </option>
            <option value="total">
              Worked hours
            </option>
            <option value="id">
              Staff ID
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Main Content (render when not loading) -->
    <div class="space-y-6 pt-4">
      <!-- Staff calendar & gauge grid -->
      <div
        v-if="viewMode === 'heatmap'"
        class="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <StaffHeatmapCard
          v-for="staff in sortedStaff"
          :key="staff.id"
          :staff="staff"
          :weeks="weeks"
          :weekdays="weekdays"
          :show-weekly-gauges="showWeeklyGauges"
          :daily-stats-by-staff="eiStore.dailyStatsByStaff"
          :daily-stats-by-staff-date-index="eiStore.dailyStatsByStaffDateIndex"
          :loading-weeks="eiStore.dailyStatsByStaffLoadingWeeks"
          :is-collapsed="isGaugeCollapsed(staff.id)"
          :include-client-as-billable="!!includeClientAsBillable[staff.id]"
          :target-pct="targetPctForStaff(staff.id)"
          @toggle-gauge="toggleGauge"
          @update-include-client="setIncludeClientAsBillable"
          @update-target-pct="setTargetPctForStaff"
        />
      </div>

      <!-- Staff bar graph list -->
      <div
        v-else
        class="space-y-6"
      >
        <div class="flex items-center gap-3 pt-3">
          <div class="text-xs font-semibold text-gray-600 whitespace-nowrap">
            Page {{ barPage }} of {{ barTotalPages }}
          </div>
          <div class="flex flex-1 items-center justify-center gap-4">
            <div class="flex items-center gap-2 text-xs text-gray-600">
              <label class="font-semibold">Per page</label>
              <select
                v-model.number="barPageSize"
                class="rounded border border-gray-300 px-2 py-1 text-xs"
              >
                <option :value="1">
                  1
                </option>
                <option :value="2">
                  2
                </option>
                <option :value="4">
                  4
                </option>
                <option :value="6">
                  6
                </option>
                <option :value="8">
                  8
                </option>
              </select>
            </div>
          </div>
          <div class="flex items-center gap-2 whitespace-nowrap">
            <button
              type="button"
              class="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 disabled:opacity-50"
              :disabled="barPage <= 1"
              @click="barPage--"
            >
              Prev
            </button>
            <button
              type="button"
              class="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 disabled:opacity-50"
              :disabled="barPage >= barTotalPages"
              @click="barPage++"
            >
              Next
            </button>
          </div>
        </div>
        <StaffBarGraph
          v-for="staff in pagedBarStaff"
          :key="staff.id"
          :staff="staff"
          :weeks="weeks"
          :daily-stats-by-staff="eiStore.dailyStatsByStaff"
          :daily-stats-by-staff-date-index="eiStore.dailyStatsByStaffDateIndex"
          :max-total-hrs="maxTotalHrs"
          :is-collapsed="isGaugeCollapsed(staff.id)"
          :include-client-as-billable="!!includeClientAsBillable[staff.id]"
          @toggle-gauge="toggleGauge"
          @update-include-client="setIncludeClientAsBillable"
          @show-tooltip="showBarTooltip"
          @hide-tooltip="hideBarTooltip"
        />
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="barTooltip.visible"
        class="fixed z-[9999] bg-white text-xs p-2 rounded-lg shadow-lg whitespace-nowrap"
        :style="{ left: `${barTooltip.x}px`, top: `${barTooltip.y}px` }"
      >
        <div class="font-semibold mb-1">
          {{ barTooltip.title }}
        </div>
        <div>{{ barTooltip.body }}</div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, onMounted, reactive, nextTick } from 'vue'
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore'
import StaffBarGraph from '@/EmployeeInsightsComponents/StaffBarGraph.vue'
import StaffHeatmapCard from '@/EmployeeInsightsComponents/StaffHeatmapCard.vue'
import {
  WEEKDAYS,
  buildCalendarWeeks,
  dateRangeToIsoDays,
  staffRangeTotals as calculateStaffRangeTotals
} from '@/utils/employeeInsightsCalculations'

const props = defineProps({
  excludedIds: {
    type: Array,
    default: () => []
  },
  viewMode: {
    type: String,
    default: null
  }
})
const emit = defineEmits(['update:viewMode'])

const eiStore   = useEmployeeInsightsStore()
const targetPct = ref(75)
const staffTargetPct = reactive(loadStaffTargetPct())
const includeClientAsBillable = reactive({})
const storedViewMode = (() => {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem('employeeInsightsViewMode')
})()
const internalViewMode = ref(storedViewMode === 'heatmap' ? 'heatmap' : 'bar')
const viewMode = computed({
  get: () => props.viewMode || internalViewMode.value,
  set: (mode) => {
    internalViewMode.value = mode
    emit('update:viewMode', mode)
  }
})
const barPage = ref(1)
const barPageSize = ref(8)
const barSortKey = ref('name')
const barTooltip = reactive({
  visible: false,
  x: 0,
  y: 0,
  title: '',
  body: ''
})
const collapsedGauges = reactive({})
let dailyStatsRenderStartedAt = null

function measuredComputed(label, computeValue, meta = () => ({}), thresholdMs = 2) {
  const startedAt = performance.now()
  const value = computeValue()
  const durationMs = Math.round(performance.now() - startedAt)
  if (durationMs >= thresholdMs) {
    console.info(`[Staff Insights] computed ${label}`, {
      durationMs,
      ...meta(value)
    })
  }
  return value
}

function countStatsByStaff(dailyStatsByStaff) {
  return Object.values(dailyStatsByStaff || {}).reduce((total, stats) => total + stats.length, 0)
}

function logAfterBrowserPaint(label, renderStartedAt, details) {
  nextTick(() => {
    requestAnimationFrame(() => {
      const completedAt = performance.now()
      console.info(label, {
        durationMs: renderStartedAt ? Math.round(completedAt - renderStartedAt) : null,
        paintEstimateMs: renderStartedAt ? Math.round(completedAt - renderStartedAt) : null,
        ...details()
      })
    })
  })
}

const isGaugeCollapsed = (staffId) => !!collapsedGauges[staffId]
const toggleGauge = (staffId) => {
  collapsedGauges[staffId] = !collapsedGauges[staffId]
}
const setIncludeClientAsBillable = (staffId, checked) => {
  includeClientAsBillable[staffId] = checked
}

function loadStaffTargetPct() {
  if (typeof window === 'undefined') return {}
  try {
    const parsed = JSON.parse(window.localStorage.getItem('employeeInsightsBillableTargets') || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (err) {
    console.warn('Failed to load employee insights billable targets:', err)
    return {}
  }
}

function persistStaffTargetPct() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem('employeeInsightsBillableTargets', JSON.stringify(staffTargetPct))
  } catch (err) {
    console.warn('Failed to persist employee insights billable targets:', err)
  }
}

function targetPctForStaff(staffId) {
  const value = Number(staffTargetPct[staffId])
  return Number.isFinite(value) ? value : targetPct.value
}

function setTargetPctForStaff(staffId, value) {
  const normalized = Math.max(0, Math.min(100, Number(value)))
  staffTargetPct[staffId] = Number.isFinite(normalized) ? normalized : targetPct.value
  persistStaffTargetPct()
}

onMounted(() => {
  eiStore.loadWeeklyHourTargets()
})

const weekdays = WEEKDAYS

const days = computed(() => measuredComputed(
  'date range days',
  () => {
    const { startDate, endDate } = eiStore
    return dateRangeToIsoDays(startDate, endDate)
  },
  (value) => ({
    dayCount: value.length,
    startDate: eiStore.startDate,
    endDate: eiStore.endDate
  })
))

const weeks = computed(() => measuredComputed(
  'calendar weeks',
  () => buildCalendarWeeks(days.value),
  (value) => ({
    weekCount: value.length,
    dayCellCount: value.reduce((total, week) => total + week.cells.length, 0),
    actualDateCount: value.reduce((total, week) => total + week.actualDates.length, 0)
  })
))

const showWeeklyGauges = computed(() => weeks.value.length > 1)

// filter out excluded
const visibleStaff = computed(() => measuredComputed(
  'visible staff',
  () => eiStore.staffList.filter(s => !props.excludedIds.includes(s.id)),
  (value) => ({
    staffCount: value.length,
    sourceStaffCount: eiStore.staffList.length,
    excludedCount: props.excludedIds.length
  })
))
const sortedStaff = computed(() => measuredComputed(
  'sorted staff',
  () => {
    const base = [...visibleStaff.value]
    if (barSortKey.value === 'id') {
      return base.sort((a, b) => Number(a.id) - Number(b.id))
    }
    if (barSortKey.value === 'name') {
      return base.sort((a, b) => {
        const an = `${a.firstname || ''} ${a.surname || ''}`.toLowerCase()
        const bn = `${b.firstname || ''} ${b.surname || ''}`.toLowerCase()
        return an.localeCompare(bn)
      })
    }
    if (barSortKey.value === 'billable') {
      return base.sort((a, b) => barStaffBillable(b.id) - barStaffBillable(a.id))
    }
    if (barSortKey.value === 'total') {
      return base.sort((a, b) => barStaffTotal(b.id) - barStaffTotal(a.id))
    }
    return base
  },
  (value) => ({
    staffCount: value.length,
    sortKey: barSortKey.value,
    statCount: countStatsByStaff(eiStore.dailyStatsByStaff)
  })
))
const barTotalPages = computed(() =>
  Math.max(1, Math.ceil(visibleStaff.value.length / barPageSize.value))
)
const pagedBarStaff = computed(() => measuredComputed(
  'paged bar staff',
  () => {
    const start = (barPage.value - 1) * barPageSize.value
    return sortedStaff.value.slice(start, start + barPageSize.value)
  },
  (value) => ({
    page: barPage.value,
    pageSize: barPageSize.value,
    renderedStaffCount: value.length
  })
))

const maxTotalHrs = computed(() => measuredComputed(
  'max total hours',
  () => {
    let max = 0
    for (const staff of visibleStaff.value) {
      const stats = eiStore.dailyStatsByStaff?.[staff.id] || []
      for (const stat of stats) {
        max = Math.max(max, stat.totalHrs || 0)
      }
    }
    return max || 1
  },
  (value) => ({
    maxTotalHrs: value,
    staffCount: visibleStaff.value.length,
    statCount: countStatsByStaff(eiStore.dailyStatsByStaff)
  })
))

function showBarTooltip({ event, title, body }) {
  const offset = 12
  barTooltip.title = title
  barTooltip.body = body
  barTooltip.x = event.clientX + offset
  barTooltip.y = event.clientY - offset
  barTooltip.visible = true
}

function hideBarTooltip() {
  barTooltip.visible = false
}

// fetch stats when date range changes
watch(
  () => [eiStore.startDate, eiStore.endDate],
  async ([s,e]) => {
    const watcherStartedAt = performance.now()
    console.info('[Staff Insights] date range watcher fired', {
      durationMs: 0,
      startDate: s,
      endDate: e
    })
    if (s && e) {
      try {
        await eiStore.fetchDailyStatsByStaff({
          startDate: s,
          endDate: e,
          progressive: true
        })
        console.info('[Staff Insights] date range watcher fetch completed', {
          durationMs: Math.round(performance.now() - watcherStartedAt),
          startDate: s,
          endDate: e
        })
      } catch (error) {
        console.error('[Staff Insights] date range watcher fetch failed', {
          durationMs: Math.round(performance.now() - watcherStartedAt),
          startDate: s,
          endDate: e,
          message: error?.message || error
        })
      }
    }
  },
  { immediate: true }
)

watch(
  () => eiStore.dailyStatsByStaff,
  (dailyStatsByStaff) => {
    dailyStatsRenderStartedAt = performance.now()
    console.info('[Staff Insights] heatmap received dailyStatsByStaff update', {
      durationMs: 0,
      staffCount: Object.keys(dailyStatsByStaff || {}).length,
      statCount: countStatsByStaff(dailyStatsByStaff),
      visibleStaffCount: visibleStaff.value.length,
      weekCount: weeks.value.length,
      dateCellCount: weeks.value.reduce((total, week) => total + week.cells.length, 0),
      estimatedHeatmapCellCount: sortedStaff.value.length * weeks.value.reduce((total, week) => total + week.cells.length, 0),
      estimatedWeeklyGaugeCount: showWeeklyGauges.value ? sortedStaff.value.length * weeks.value.length : 0,
      estimatedBarCount: pagedBarStaff.value.length * weeks.value.reduce((total, week) => total + week.actualDates.length, 0),
      viewMode: viewMode.value
    })
    logAfterBrowserPaint(
      '[Staff Insights] heatmap browser paint estimate completed',
      dailyStatsRenderStartedAt,
      () => ({
        renderedStaffCount: viewMode.value === 'bar' ? pagedBarStaff.value.length : sortedStaff.value.length,
        visibleStaffCount: visibleStaff.value.length,
        weekCount: weeks.value.length,
        viewMode: viewMode.value
      })
    )
  },
  { deep: false }
)

watch(
  [viewMode, sortedStaff, pagedBarStaff, weeks, showWeeklyGauges],
  () => {
    const renderStartedAt = performance.now()
    console.info('[Staff Insights] render volume changed', {
      durationMs: 0,
      viewMode: viewMode.value,
      visibleStaffCount: visibleStaff.value.length,
      renderedStaffCount: viewMode.value === 'bar' ? pagedBarStaff.value.length : sortedStaff.value.length,
      weekCount: weeks.value.length,
      dateCellCountPerStaff: weeks.value.reduce((total, week) => total + week.cells.length, 0),
      actualDateCountPerStaff: weeks.value.reduce((total, week) => total + week.actualDates.length, 0),
      estimatedHeatmapCellCount: sortedStaff.value.length * weeks.value.reduce((total, week) => total + week.cells.length, 0),
      estimatedWeeklyGaugeCount: showWeeklyGauges.value ? sortedStaff.value.length * weeks.value.length : 0,
      estimatedBarCount: pagedBarStaff.value.length * weeks.value.reduce((total, week) => total + week.actualDates.length, 0)
    })
    logAfterBrowserPaint(
      '[Staff Insights] render volume browser paint estimate completed',
      renderStartedAt,
      () => ({
        viewMode: viewMode.value,
        renderedStaffCount: viewMode.value === 'bar' ? pagedBarStaff.value.length : sortedStaff.value.length
      })
    )
  },
  { immediate: true }
)

watch([barPageSize, visibleStaff, barSortKey], () => {
  if (barPage.value > barTotalPages.value) {
    barPage.value = barTotalPages.value
  }
})

watch(viewMode, (mode) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem('employeeInsightsViewMode', mode)
  } catch (err) {
    console.warn('Failed to persist employee insights view mode:', err)
  }
})

function barStaffBillable(staffId) {
  return staffRangeTotals(staffId).billable
}

function barStaffTotal(staffId) {
  return staffRangeTotals(staffId).total
}

function staffRangeTotals(staffId) {
  return calculateStaffRangeTotals(eiStore.dailyStatsByStaff, staffId)
}

</script>

<style scoped>
/* Reverse spin (for LoadingSpinner inner) */
@keyframes spin-reverse {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
}
.animate-spin-reverse {
  animation: spin-reverse 1.5s linear infinite;
}

</style>
